import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  Switch,
  Select,
  MenuItem,
  Slider,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ColorPicker from '../../../../components/kea/ColorPicker';
import IntlMessages from 'util/IntlMessages';

export const useCompactPropertyFieldStyles = makeStyles((theme) => ({
  field: {
    padding: theme.spacing(1.5, 0),
    borderBottom: '1px solid #edf2f0',
    '&:last-child': {
      borderBottom: 0,
    },
  },
  label: {
    color: '#26344f',
    fontSize: '0.8125rem',
    fontWeight: 600,
    lineHeight: 1.4,
    marginBottom: theme.spacing(0.5),
    display: 'block',
  },
  description: {
    maxWidth: '32rem',
    fontSize: '0.6875rem',
    lineHeight: 1.45,
    color: '#74817b',
    marginBottom: theme.spacing(1),
  },
  sliderContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sliderValue: {
    minWidth: 38,
    padding: '3px 7px',
    border: '1px solid #dce7e3',
    borderRadius: 7,
    background: '#f7faf9',
    color: '#226b51',
    textAlign: 'center',
    fontSize: '0.75rem',
    fontWeight: 700,
  },
  toggleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  input: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      background: '#fff',
    },
  },
  select: {
    height: 40,
    borderRadius: 8,
    background: '#fff',
    color: '#26344f',
    fontSize: '0.8125rem',
    fontWeight: 500,
    '& [role="button"]': {
      height: '38px',
      minHeight: '38px',
      padding: '10px 36px 10px 12px !important',
      boxSizing: 'border-box',
      fontSize: '0.8125rem',
      lineHeight: '18px',
    },
    '& fieldset': {
      borderColor: '#d8e3df',
    },
    '&:hover fieldset': {
      borderColor: '#9ab9ad',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'var(--kea-color-primary)',
      borderWidth: 1,
    },
    '& > svg': {
      color: '#66756f',
      right: 9,
    },
  },
  selectMenu: {
    marginTop: 4,
    border: '1px solid #dfe8e5',
    borderRadius: 8,
    boxShadow: '0 10px 24px rgba(24, 58, 46, 0.14)',
  },
  selectMenuItem: {
    minHeight: 36,
    padding: '8px 12px',
    color: '#26344f',
    fontSize: '0.8125rem',
    lineHeight: 1.35,
    '&.Mui-selected': {
      backgroundColor: '#edf8f4',
      color: '#176f50',
      fontWeight: 600,
    },
    '&.Mui-selected:hover': {
      backgroundColor: '#e3f4ed',
    },
  },
  codeInput: {
    '& textarea': {
      fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", monospace',
      fontSize: '0.75rem',
      lineHeight: 1.55,
    },
  },
}));

/**
 * Compact Text Input - for narrow Properties Editor column
 */
export const CompactTextInput = ({
  title, description, value, onChange, multiline,
}) => {
  const classes = useCompactPropertyFieldStyles();
  return (
    <Box className={classes.field}>
      <Typography className={classes.label}>{title}</Typography>
      {description && <Typography className={classes.description}>{description}</Typography>}
      <TextField
        fullWidth
        className={`${classes.input} ${multiline ? classes.codeInput : ''}`}
        size="small"
        variant="outlined"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        multiline={multiline}
        minRows={multiline ? 5 : undefined}
        inputProps={{ 'aria-label': title }}
      />
    </Box>
  );
};

CompactTextInput.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  multiline: PropTypes.bool,
};

CompactTextInput.defaultProps = {
  description: '',
  value: '',
  multiline: false,
};

/**
 * Compact Range Slider - for narrow Properties Editor column
 */
export const CompactRangeSlider = ({
  title,
  description,
  value,
  min,
  max,
  step,
  onChange,
}) => {
  const classes = useCompactPropertyFieldStyles();
  return (
    <Box className={classes.field}>
      <Typography className={classes.label}>{title}</Typography>
      {description && <Typography className={classes.description}>{description}</Typography>}
      <Box className={classes.sliderContainer}>
        <Slider
          value={value ?? 0}
          onChange={(e, newValue) => onChange(newValue)}
          min={min}
          max={max}
          step={step}
          valueLabelDisplay="auto"
          aria-label={title}
        />
        <Typography className={classes.sliderValue}>{value ?? 0}</Typography>
      </Box>
    </Box>
  );
};

CompactRangeSlider.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  value: PropTypes.number,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  step: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

CompactRangeSlider.defaultProps = {
  description: '',
  value: 0,
};

/**
 * Compact Toggle Switch - for narrow Properties Editor column
 */
export const CompactToggle = ({ title, description, value, onChange }) => {
  const classes = useCompactPropertyFieldStyles();
  return (
    <Box className={classes.field}>
      <Box className={classes.toggleContainer}>
        <Box>
          <Typography className={classes.label}>{title}</Typography>
          {description && <Typography className={classes.description}>{description}</Typography>}
        </Box>
        <Switch
          checked={value || false}
          onChange={(e) => onChange(e.target.checked)}
          color="primary"
          inputProps={{ 'aria-label': title }}
        />
      </Box>
    </Box>
  );
};

CompactToggle.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  value: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

CompactToggle.defaultProps = {
  description: '',
  value: false,
};

/**
 * Compact Select Dropdown - for narrow Properties Editor column
 */
export const CompactSelect = ({
  title,
  description,
  value,
  items,
  onChange,
  nullable,
}) => {
  const classes = useCompactPropertyFieldStyles();
  return (
    <Box className={classes.field}>
      <Typography className={classes.label}>{title}</Typography>
      {description && <Typography className={classes.description}>{description}</Typography>}
      <Select
        fullWidth
        className={classes.select}
        variant="outlined"
        value={value == null ? '' : value}
        onChange={(e) => onChange(nullable && e.target.value === '' ? null : e.target.value)}
        inputProps={{ 'aria-label': title }}
        MenuProps={{
          classes: { paper: classes.selectMenu },
          getContentAnchorEl: null,
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
        }}
      >
        {nullable && (
          <MenuItem className={classes.selectMenuItem} value="">
            <IntlMessages id="ui.searchExperienceV2.editor.properties.compactPropertyFields.useDefault" />
          </MenuItem>
        )}
        {items.map((item) => (
          <MenuItem className={classes.selectMenuItem} key={item.value} value={item.value}>
            {item.text}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

CompactSelect.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any.isRequired,
      text: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  nullable: PropTypes.bool,
};

CompactSelect.defaultProps = {
  description: '',
  value: '',
  nullable: false,
};

/**
 * Compact multiple-choice editor for canonical `array_enum` descriptors.
 */
export const CompactMultiSelect = ({ title, description, value, items, onChange }) => {
  const classes = useCompactPropertyFieldStyles();
  const selected = Array.isArray(value) ? value : [];
  return (
    <Box className={classes.field}>
      <Typography className={classes.label}>{title}</Typography>
      {description && <Typography className={classes.description}>{description}</Typography>}
      <Select
        multiple
        fullWidth
        className={classes.select}
        variant="outlined"
        value={selected}
        onChange={(e) => onChange(e.target.value)}
        inputProps={{ 'aria-label': title }}
        renderValue={(values) => values.join(', ')}
        MenuProps={{
          classes: { paper: classes.selectMenu },
          getContentAnchorEl: null,
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
        }}
      >
        {items.map((item) => (
          <MenuItem className={classes.selectMenuItem} key={item.value} value={item.value}>
            {item.text}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

CompactMultiSelect.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.string),
  items: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  })).isRequired,
  onChange: PropTypes.func.isRequired,
};

CompactMultiSelect.defaultProps = {
  description: '',
  value: [],
};

/**
 * The gateway represents simple string lists as `array_of_strings`. A compact
 * comma-separated input keeps every value editable without inventing a second
 * list-builder interaction inside the narrow properties column.
 */
export const CompactStringListInput = ({ title, description, value, onChange }) => {
  const classes = useCompactPropertyFieldStyles();
  const inputValue = Array.isArray(value) ? value.join(', ') : '';
  return (
    <Box className={classes.field}>
      <Typography className={classes.label}>{title}</Typography>
      {description && <Typography className={classes.description}>{description}</Typography>}
      <TextField
        fullWidth
        className={classes.input}
        size="small"
        variant="outlined"
        value={inputValue}
        onChange={(e) => onChange(
          e.target.value.split(',').map(item => item.trim()).filter(Boolean),
        )}
        inputProps={{ 'aria-label': title }}
      />
    </Box>
  );
};

CompactStringListInput.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
};

CompactStringListInput.defaultProps = {
  description: '',
  value: [],
};

/**
 * Compact Color Picker - for narrow Properties Editor column
 */
export const CompactColorPicker = ({ title, description, value, onChange }) => {
  const classes = useCompactPropertyFieldStyles();
  return (
    <Box className={classes.field}>
      <Typography className={classes.label}>{title}</Typography>
      {description && <Typography className={classes.description}>{description}</Typography>}
      <ColorPicker value={value || '#000000'} onChange={onChange} />
    </Box>
  );
};

CompactColorPicker.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

CompactColorPicker.defaultProps = {
  description: '',
  value: '#000000',
};
